import { useRef, useEffect, useState, useMemo, useCallback, ReactNode } from 'react'
import { TaskListItem } from '@myapp/contract'
import { useSchedule, selectTechnicianSchedule } from '../../../entities/schedule'
import { useTechnicianTasks } from '../../../entities/task'
import { useUpsertBreak } from "../../../entities/schedule"
import { CalendarHeader } from '../../worker-calendar/ui/CalendarHeader'
import { CalendarTimeAxis } from '../../worker-calendar/ui/CalendarTimeAxis'
import { useLocalCalendarDays } from '../model/useLocalCalendarDays'
import { checkCollisions } from '../model/checkCollisions'
import { usePlanningDrag, DragType } from '../model/usePlanningDrag'
import { PlanningDayColumn } from './PlanningDayColumn'
import { DurationInputs } from './DurationInputs'
import { ConflictWarning } from '../../../shared/ui/feedback/ConflictWarning/ConflictWarning'
import type { TechnicianScheduleView } from '../../../entities/schedule'

const TIME_AXIS_WIDTH = 48

export interface SchedulePayload {
  plannedStart: string
  plannedWorkDuration: number
  plannedTravelDuration: number
  plannedDepartureDuration: number
}

export interface CalendarPlanningOverlayProps {
  show: boolean
  onClose: () => void
  technicianId: number

  /** Pre-selected work start ISO. Pass null for no pre-selection. */
  initialSelectedDate?: string | null
  initialWorkDuration?: number
  initialTravelDuration?: number
  initialDepartureDuration?: number
  /** When true, resets durations and selectedDate each time the overlay opens. */
  resetOnShow?: boolean

  /** Optional filter applied to existing tasks before rendering and collision checks. */
  filterExistingTasks?: (tasks: TaskListItem[]) => TaskListItem[]
  /** Appointment ID to exclude from rendering and collision checks (use when rescheduling an existing appointment). */
  excludeAppointmentId?: number

  headerTitle: string
  saveButtonColor: string
  draftTitle: string
  draftColors?: { work: string; travel: string }
  hintText: string

  /** Called with the scheduled payload. Overlay calls onClose() after this resolves. */
  onSave: (payload: SchedulePayload) => Promise<void>
  isPending: boolean

  /**
   * Render slot for extra content between ConflictWarning and the hint text.
   * Receives the current selectedDate so DeadlineWarning can react to it.
   */
  bottomExtra?: (state: { selectedDate: string | null }) => ReactNode
}

export function CalendarPlanningOverlay({
  show,
  onClose,
  technicianId,
  initialSelectedDate = null,
  initialWorkDuration = 60,
  initialTravelDuration = 30,
  initialDepartureDuration = 30,
  resetOnShow = false,
  filterExistingTasks,
  excludeAppointmentId,
  headerTitle,
  saveButtonColor,
  draftTitle,
  draftColors,
  hintText,
  onSave,
  isPending,
  bottomExtra,
}: CalendarPlanningOverlayProps) {
  const { days, dayIsos, from, to, todayIso, prev, next, goToToday } = useLocalCalendarDays()
  const scrollRef = useRef<HTMLDivElement>(null)
  const didScrollRef = useRef(false)

  const [selectedDate, setSelectedDate] = useState<string | null>(initialSelectedDate)
  const [workDuration, setWorkDuration] = useState(initialWorkDuration)
  const [travelDuration, setTravelDuration] = useState(initialTravelDuration)
  const [departureDuration, setDepartureDuration] = useState(initialDepartureDuration)

  const upsertBreak = useUpsertBreak()

  // Reset scroll flag on close; optionally reset state on open
  useEffect(() => {
    if (!show) {
      didScrollRef.current = false
      return
    }
    if (resetOnShow) {
      setSelectedDate(initialSelectedDate ?? null)
      setWorkDuration(initialWorkDuration ?? 60)
      setTravelDuration(initialTravelDuration ?? 30)
      setDepartureDuration(initialDepartureDuration ?? 30)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  // Auto-scroll to current time on open
  useEffect(() => {
    if (!show) return
    if (didScrollRef.current) return
    const el = scrollRef.current
    if (!el) return
    const now = new Date()
    const hourDecimal = now.getHours() + now.getMinutes() / 60
    const px = (hourDecimal - 6) * 64
    el.scrollTop = Math.max(0, px - 150)
    didScrollRef.current = true
  }, [show])

  const { data: tasksData } = useTechnicianTasks(technicianId, { from, to })
  const rawTasks = tasksData?.scheduledTasks ?? []
  const existingTasks = filterExistingTasks ? filterExistingTasks(rawTasks) : rawTasks

  const { data: scheduleResult } = useSchedule({
    from,
    to,
    technicianIds: [Number(technicianId)],
  })
  const rawTechnicianData = useMemo(
    () => selectTechnicianSchedule(scheduleResult, technicianId),
    [scheduleResult, technicianId],
  )

  // Strip the appointment being rescheduled so it doesn't appear in the calendar or trigger a self-conflict
  const technicianData = useMemo((): TechnicianScheduleView | null => {
    if (!rawTechnicianData || !excludeAppointmentId) return rawTechnicianData
    return {
      ...rawTechnicianData,
      appointments: rawTechnicianData.appointments.filter((a) => a.id !== excludeAppointmentId),
    }
  }, [rawTechnicianData, excludeAppointmentId])

  const conflicts = useMemo(() => {
    if (!selectedDate) return []
    return checkCollisions(
      { plannedStart: selectedDate, travelDuration, workDuration, departureDuration },
      technicianData,
      existingTasks,
    )
  }, [selectedDate, travelDuration, workDuration, departureDuration, technicianData, existingTasks])

  // --- Drag-and-drop ---
  const handleDrop = useCallback(
    (type: DragType, dayIso: string, hours: number, minutes: number) => {
      if (type === 'draft') {
        // Dropped position = travel start → compute work start
        const travelStart = new Date(`${dayIso}T00:00:00`)
        travelStart.setHours(hours, minutes, 0, 0)
        const workStart = new Date(travelStart.getTime() + travelDuration * 60_000)
        setSelectedDate(workStart.toISOString())
      } else if (type === 'break') {
        const breakStart = new Date(`${dayIso}T00:00:00`)
        breakStart.setHours(hours, minutes, 0, 0)
        upsertBreak.mutate({
          technicianId: technicianId,
          startsAt: breakStart.toISOString(),
        })
      }
    },
    [travelDuration, technicianId, upsertBreak],
  )

  const { drag, startDrag } = usePlanningDrag(handleDrop)

  // Prevent pinch-to-zoom while the overlay is open so it doesn't interfere with drag
  useEffect(() => {
    if (!show) return
    const preventZoom = (e: TouchEvent) => {
      if (e.touches.length > 1) e.preventDefault()
    }
    const preventGesture = (e: Event) => e.preventDefault()
    document.addEventListener('touchstart', preventZoom, { passive: false })
    document.addEventListener('touchmove', preventZoom, { passive: false })
    document.addEventListener('gesturestart', preventGesture, { passive: false })
    document.addEventListener('gesturechange', preventGesture, { passive: false })
    return () => {
      document.removeEventListener('touchstart', preventZoom)
      document.removeEventListener('touchmove', preventZoom)
      document.removeEventListener('gesturestart', preventGesture)
      document.removeEventListener('gesturechange', preventGesture)
    }
  }, [show])

  function handleTravelChange(newTravel: number) {
    setSelectedDate((prev) => {
      if (!prev) return prev
      const oldWorkStart = new Date(prev).getTime()
      const travelStart = oldWorkStart - travelDuration * 60_000
      return new Date(travelStart + newTravel * 60_000).toISOString()
    })
    setTravelDuration(newTravel)
  }

  function handleTimeSelect(dayIso: string, hours: number, minutes: number) {
    const travelStart = new Date(`${dayIso}T00:00:00`)
    travelStart.setHours(hours, minutes, 0, 0)
    const workStart = new Date(travelStart.getTime() + travelDuration * 60_000)
    setSelectedDate(workStart.toISOString())
  }

  async function handleSave() {
    if (!selectedDate) return
    await onSave({
      plannedStart: selectedDate,
      plannedWorkDuration: workDuration,
      plannedTravelDuration: travelDuration,
      plannedDepartureDuration: departureDuration,
    })
    onClose()
  }

  if (!show) return null

  const draft = selectedDate
    ? {
        plannedStart: selectedDate,
        workDuration,
        travelDuration,
        departureDuration,
        title: draftTitle,
      }
    : null

  return (
    <div className="fixed inset-0 z-[150] bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 shrink-0">
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded"
          aria-label="Schließen"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path d="M3 3L15 15M15 3L3 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <span className="flex-1 text-base font-semibold text-gray-800">{headerTitle}</span>
        <button
          onClick={handleSave}
          disabled={!selectedDate || isPending || conflicts.length > 0}
          className="px-4 py-1.5 rounded-lg text-white text-sm font-semibold disabled:opacity-40"
          style={{ backgroundColor: saveButtonColor }}
        >
          {isPending ? 'Speichern…' : 'Speichern'}
        </button>
      </div>

      {/* Calendar navigation header */}
      <div className="shrink-0">
        {/*
          No `onRefresh`, so no freshness badge here — this overlay picks a slot, it does not
          stand in for the calendar, and it has no manual refetch to offer.
        */}
        <CalendarHeader
          days={days}
          todayIso={todayIso}
          onPrev={prev}
          onNext={next}
          onToday={goToToday}
        />
      </div>

      {/* Scrollable calendar */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0">
        <div className="flex" style={{ minHeight: 1024 }}>
          <div
            className="shrink-0 border-r border-gray-200 bg-white"
            style={{ width: TIME_AXIS_WIDTH }}
          >
            <div className="sticky top-0 z-50 bg-white border-b border-gray-200" style={{ height: 62 }} />
            <CalendarTimeAxis />
          </div>

          <div className="flex flex-1 min-w-0">
            {dayIsos.map((iso) => (
              <PlanningDayColumn
                key={iso}
                dayIso={iso}
                isToday={iso === todayIso}
                technicianData={technicianData}
                existingTasks={existingTasks}
                currentUserId={technicianId}
                draft={draft}
                onTimeSelect={handleTimeSelect}
                draftColors={draftColors}
                drag={drag}
                onDragStart={startDrag}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Duration controls */}
      <div className="shrink-0">
        <DurationInputs
          workDuration={workDuration}
          travelDuration={travelDuration}
          departureDuration={departureDuration}
          onWorkChange={setWorkDuration}
          onTravelChange={handleTravelChange}
          onDepartureChange={setDepartureDuration}
        />

        <ConflictWarning conflicts={conflicts} />

        {bottomExtra?.({ selectedDate })}

        {!selectedDate && (
          <p className="text-center text-xs text-gray-400 pb-3">{hintText}</p>
        )}
      </div>
    </div>
  )
}
